import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

export function ClosingCTASection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subheadlineRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headlineRef.current,
        { y: '-6vh', opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            end: 'top 50%',
            scrub: true,
          },
        }
      );

      gsap.fromTo(
        subheadlineRef.current,
        { y: '6vh', opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            end: 'top 40%',
            scrub: true,
          },
        }
      );

      gsap.fromTo(
        ctaRef.current,
        { y: '6vh', opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
            end: 'top 30%',
            scrub: true,
          },
        }
      );

      gsap.fromTo(
        footerRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: footerRef.current,
            start: 'top 90%',
            end: 'top 70%',
            scrub: true,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-ink relative z-[80]"
      style={{ minHeight: '110vh' }}
    >
      {/* Paper texture overlay for ink bg */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'screen',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-8">
        {/* Headline */}
        <h2
          ref={headlineRef}
          className="font-display text-center font-bold leading-[1.05] tracking-[-0.02em] text-white"
          style={{ fontSize: 'clamp(44px, 5vw, 72px)', marginTop: '22vh' }}
        >
          Start building receipts.
        </h2>

        {/* Subheadline */}
        <p
          ref={subheadlineRef}
          className="mt-6 max-w-xl text-center leading-relaxed text-[#9CA3AF]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)' }}
        >
          Create an account and start saving evidence trails.
        </p>

        <div ref={ctaRef} className="mt-10 flex items-center gap-4">
          <Link
            to="/signup"
            className="btn-hover flex items-center gap-2 rounded-full bg-[#E1062C] px-6 py-4 font-medium text-white hover:bg-[#c00525]"
          >
            Create free account
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/signin"
            className="rounded-full border border-white/20 px-6 py-4 font-medium text-white transition-colors hover:border-white/40 hover:bg-white/10"
          >
            Sign in
          </Link>
        </div>

        {/* Footer */}
        <div
          ref={footerRef}
          className="mt-auto flex w-full items-center justify-between pb-8 pt-16 text-sm text-[#6B6F78]"
        >
          <span>© 2026 Duly Cited</span>
          <span>Official sources linked on every receipt</span>
        </div>
      </div>
    </section>
  );
}
